import { useEffect, useState } from "react";
import type { Bootstrap, BudgetItem, BudgetResult } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, todayISO, parseMoney } from "../lib/format";
import StatCard from "../components/StatCard";
import Modal from "../components/Modal";
import Select from "../components/Select";
import BudgetModal from "../components/BudgetModal";

const CADENCE_LABEL: Record<BudgetItem["cadence"], string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  once: "One-off",
};

// Budget — plan your everyday discretionary spending in buckets (Weekly shop,
// Fuel, Presents…), then log the real spends against each. Ties into Safe to
// spend to show what's genuinely free to save.
export default function Budget({ data, reload }: { data: Bootstrap | null; reload: () => Promise<void> }) {
  const [budget, setBudget] = useState<BudgetResult | null>(null);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState<BudgetItem | null>(null);
  const [spendFor, setSpendFor] = useState<BudgetItem | null>(null);

  async function load() {
    setBudget(await api.listBudget());
  }
  useEffect(() => {
    load();
  }, []);

  if (!data) return <div className="state">Loading…</div>;

  // "Safe to spend" is a right-now figure (money in the account today minus bills
  // still to pay). For a month that hasn't started it's meaningless — that month's
  // income isn't in the account yet — so we show the month's projection instead,
  // exactly like This Month does.
  const inThisCycle = todayISO() >= data.summary.cycleStart && todayISO() <= data.summary.cycleEnd;
  const safe = data.funds.safeToSpendMinor;
  const plannedTotal = budget?.totalPlannedMinor ?? 0;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <p className="page__eyebrow">Budget</p>
          <h1 className="page__title">Planned spending</h1>
          <p className="page__lede">
            Plan the everyday essentials, then log the actual spends against each bucket to see where you really are.
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditItem(null); setModal(true); }}>+ Add budget</button>
      </div>

      <section className="stat-grid">
        <StatCard
          label="Left after everything"
          value={formatMoney(data.summary.leftAfterEverythingMinor)}
          sub="Income − all bills"
          tone={data.summary.leftAfterEverythingMinor >= 0 ? "positive" : "negative"}
        />
        {inThisCycle ? (
          <StatCard
            label="Safe to spend"
            value={safe != null ? formatMoney(safe) : "—"}
            sub={safe != null ? "After all bills" : "Set your balance"}
          />
        ) : (
          <StatCard
            label="Budget planned"
            value={formatMoney(plannedTotal)}
            sub="Across all buckets this month"
          />
        )}
      </section>

      {/* Per-person split: what each is budgeted, and their money free to save
          after their bills and that budget (matches My Position). */}
      {budget && budget.split.length > 0 && (
        <section className="split-grid">
          {budget.split.map((s) => (
            <div className="split-card" key={s.memberId}>
              <div className="split-card__head">
                <span className="avatar avatar--xs" style={{ background: s.colour }}>{s.initial}</span>
                <span className="split-card__name">{s.name}</span>
              </div>
              <div className="split-card__figs">
                <div className="split-card__fig">
                  <span className="split-card__fig-label">Budgeted to spend</span>
                  <span className="split-card__fig-val">{formatMoney(s.budgetedMinor)}</span>
                </div>
                <div className="split-card__fig">
                  <span className="split-card__fig-label">Free to save after</span>
                  <span className={"split-card__fig-val" + (s.freeToSaveMinor == null ? "" : s.freeToSaveMinor < 0 ? " split-card__fig-val--neg" : " split-card__fig-val--pos")}>
                    {s.freeToSaveMinor != null ? formatMoney(s.freeToSaveMinor) : "—"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {budget == null ? (
        <div className="state">Loading…</div>
      ) : budget.items.length === 0 ? (
        <div className="panel empty">
          No budgets yet. Add one — Weekly shop, Fuel, Presents… — to plan and track your everyday spending.
        </div>
      ) : (
        <div className="goal-grid">
          {budget.items.map((it) => {
            const planned = it.plannedMinor;
            const remaining = planned - it.actualMinor;
            const pct = planned > 0 ? Math.min(100, Math.round((it.actualMinor / planned) * 100)) : 0;
            const over = it.actualMinor > planned;
            return (
              <section className="goal-card" key={it.id}>
                <div className="goal-card__head">
                  <div className="goal-card__title">
                    <span className="goal-card__name">{it.name}</span>
                    <span className="type-tag">{CADENCE_LABEL[it.cadence]}</span>
                    {it.categoryName && (
                      <span className="chip"><span className="chip__dot" style={{ background: it.categoryColour ?? "#98A2B3" }} />{it.categoryName}</span>
                    )}
                    {it.paidByInitial && (
                      <span className="chip"><span className="avatar avatar--xs" style={{ background: it.paidByColour ?? "#98A2B3" }}>{it.paidByInitial}</span>{it.paidByName}</span>
                    )}
                  </div>
                  <button className="icon-btn" onClick={() => { setEditItem(it); setModal(true); }} aria-label="Edit budget" title="Edit">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                  </button>
                </div>

                <div className="goal-card__figures">
                  <span className="goal-card__cur">{formatMoney(it.actualMinor)}</span>
                  <span className="goal-card__of">
                    of {formatMoney(planned)}
                    {it.cadence === "weekly" && ` · ${formatMoney(it.amountMinor)}/wk × ${budget.weeks}`}
                  </span>
                  <span className="goal-card__pct">{pct}%</span>
                </div>
                <div className="meter"><span style={{ width: `${pct}%`, background: over ? "var(--color-status-red)" : "var(--color-primary)" }} /></div>

                <div className="goal-card__stats">
                  <div className={"goal-card__stat" + (over ? " goal-card__stat--done" : "")}>
                    <span>{over ? "Over budget by" : "Left to spend"}</span>
                    <strong style={over ? { color: "var(--color-status-red)" } : undefined}>{formatMoney(Math.abs(remaining))}</strong>
                  </div>
                </div>

                {it.notes && <p className="goal-card__desc">{it.notes}</p>}

                <button className="btn btn--sm goal-card__add" onClick={() => setSpendFor(it)}>+ Log spend</button>
              </section>
            );
          })}
        </div>
      )}

      {modal && (
        <BudgetModal data={data} item={editItem} onClose={() => setModal(false)} onSaved={async () => { setModal(false); await load(); }} />
      )}
      {spendFor && (
        <LogBudgetSpend
          data={data}
          item={spendFor}
          onClose={() => setSpendFor(null)}
          onSaved={async () => { setSpendFor(null); await load(); await reload(); }}
        />
      )}
    </div>
  );
}

// Logs a REAL spend against a budget bucket — deducts from the balance, dated,
// and tagged with the bucket so it counts toward the bucket's "actual".
function LogBudgetSpend({
  data,
  item,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  item: BudgetItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [where, setWhere] = useState(item.name);
  const [occurredOn, setOccurredOn] = useState(todayISO());
  // Default the payer to whoever's expected to spend this bucket.
  const [paidBy, setPaidBy] = useState<string>(item.paidBy ? String(item.paidBy) : "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const minor = parseMoney(amount);
    if (minor == null || minor <= 0) return setError("Enter a valid amount");
    if (!where.trim()) return setError("Where did you spend?");
    setSaving(true);
    try {
      await api.createSpend({
        occurredOn,
        where: where.trim(),
        amountMinor: minor,
        categoryId: item.categoryId,
        paidBy: paidBy ? Number(paidBy) : null,
        offBalance: true,
        kind: "out",
        budgetItemId: item.id,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={`Log spend · ${item.name}`} onClose={onClose}>
      <div className="form">
        <p className="field__hint">This logs a real spend and deducts from your balance.</p>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Amount (£)</span>
            <input className="input" inputMode="decimal" value={amount} autoFocus onChange={(e) => setAmount(e.target.value)} placeholder="45.00" />
          </label>
          <label className="field">
            <span className="field__label">Date</span>
            <input className="input" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Where</span>
          <input className="input" value={where} onChange={(e) => setWhere(e.target.value)} placeholder="Tesco" />
        </label>
        <div className="field">
          <span className="field__label">Paid by</span>
          <Select
            value={paidBy}
            onChange={setPaidBy}
            placeholder="—"
            ariaLabel="Paid by"
            options={[{ value: "", label: "—" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
          />
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Log spend"}</button>
        </div>
      </div>
    </Modal>
  );
}
